import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight } from 'lucide-react';
import { PageContainer, PageContent } from '@/components/ui/page-layout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FACULTY_COLORS, DEFAULT_NODE_COLOR } from '../components/graph/graphConfig';
import { isPracticalType } from '../utils/practicalResults';
import { ACCENT_ACADEMIC } from '../lib/personAccents';
import { getProjectDetails, getSimilarProjects } from '../services/apiService';
import type { ProjectDetail, SimilarProject } from '../types/api';
import SimilarResultsTable from '../components/projects/SimilarResultsTable';

function facultyAbbreviation(faculty: string | null | undefined): string | null {
  if (!faculty) return null;
  const trimmed = faculty.trim();
  if (!trimmed) return null;
  if (FACULTY_COLORS[trimmed]) return trimmed;
  const upper = trimmed.toUpperCase();
  if (FACULTY_COLORS[upper]) return upper;
  return null;
}

export default function ProjectDetailPage() {
  const { t } = useTranslation('projects');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [similar, setSimilar] = useState<SimilarProject[]>([]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setProject(null);
    setSimilar([]);
    getProjectDetails(id)
      .then(data => {
        if (cancelled) return;
        if (data && data.result) {
          setProject(data.result);
        } else {
          setError('Result not found.');
        }
      })
      .catch(err => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load result details.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    getSimilarProjects(id, 50)
      .then(data => {
        if (cancelled) return;
        setSimilar(data?.results ?? []);
      })
      .catch(() => {
        if (cancelled) return;
        setSimilar([]);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const goBack = () => {
    if (location.state?.from) {
      navigate(location.state.from);
    } else {
      navigate('/outcomes');
    }
  };

  const handlePersonClick = (personId: number, e: React.MouseEvent) => {
    e.preventDefault();
    const path = `/person/${personId}`;
    if (e.ctrlKey || e.metaKey) {
      window.open(path, '_blank');
    } else {
      navigate(path);
    }
  };

  const hideBackButton = ['ctu-companies', 'modal'].includes(
    new URLSearchParams(location.search).get('from') || '',
  );

  const facultyAbbr = facultyAbbreviation(project?.faculty);
  const facultyColor = facultyAbbr ? FACULTY_COLORS[facultyAbbr] || DEFAULT_NODE_COLOR : null;
  const practical = project ? isPracticalType(project.type) : false;
  const typeBadgeStyle = project
    ? practical
      ? undefined
      : { backgroundColor: ACCENT_ACADEMIC, color: '#ffffff', borderColor: 'transparent' }
    : undefined;
  const typeBadgeClass = practical
    ? 'bg-primary text-primary-foreground border-transparent font-normal hover:bg-primary'
    : 'border-transparent font-normal text-white';

  return (
    <PageContainer>
      <PageContent>
        {!hideBackButton && (
          <Button variant="ghost" size="sm" className="mb-4" onClick={goBack}>
            ← {t('backToSearch')}
          </Button>
        )}

        {loading && (
          <div
            className="flex flex-col gap-6 py-2"
            aria-busy="true"
            aria-label={t('loadingDetails')}
          >
            <Skeleton className="h-32 w-full rounded-lg" />
            <Skeleton className="h-40 w-full rounded-lg" />
            <Skeleton className="h-28 w-full rounded-lg" />
          </div>
        )}

        {error && !loading && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {project && !loading && (
          <>
            <Card className="mb-6 border-ctu-border shadow-none">
              <CardContent className="p-8">
                <div className="mb-4 flex flex-wrap items-center gap-3">
                  {project.type && (
                    <Badge
                      variant="outline"
                      className={typeBadgeClass}
                      style={typeBadgeStyle}
                    >
                      {project.type}
                    </Badge>
                  )}
                  {project.year !== null && project.year !== undefined && (
                    <span className="text-sm font-mono tabular-nums text-ctu-text-muted">
                      {project.year}
                    </span>
                  )}
                  {facultyAbbr && facultyColor && (
                    <span
                      className="ml-auto inline-flex items-center rounded px-2 py-0.5 text-[11px] font-semibold tracking-wide text-white"
                      style={{ backgroundColor: facultyColor }}
                    >
                      {facultyAbbr}
                    </span>
                  )}
                </div>
                <h1 className="text-3xl font-semibold tracking-tight text-ctu-text leading-tight">
                  {project.name}
                </h1>
                {project.faculty && !facultyAbbr && (
                  <p className="mt-2 text-sm text-ctu-text-muted">{project.faculty}</p>
                )}
              </CardContent>
            </Card>

            <Card className="mb-6 border-ctu-border shadow-none">
              <CardContent className="p-8">
                <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted mb-3">
                  {t('detail.description')}
                </span>
                {project.description ? (
                  <p className="m-0 text-base leading-relaxed text-ctu-text whitespace-pre-line">
                    {project.description}
                  </p>
                ) : (
                  <p className="m-0 text-sm text-ctu-text-muted italic">
                    {t('detail.noDescription')}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="mb-6 border-ctu-border shadow-none">
              <CardContent className="p-8">
                <div className="mb-4 flex items-baseline gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
                    {t('detail.contributors')}
                  </span>
                  <span className="text-[11px] font-mono tabular-nums text-ctu-text-muted">
                    {project.people?.length ?? 0}
                  </span>
                </div>
                {project.people && project.people.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {project.people.map(person => {
                      const abbr = person.faculty_abbr ?? '';
                      const personFacultyColor = abbr
                        ? FACULTY_COLORS[abbr] || DEFAULT_NODE_COLOR
                        : null;
                      return (
                        <a
                          key={person.id}
                          href={`/person/${person.id}`}
                          onClick={e => handlePersonClick(person.id, e)}
                          className="group inline-flex items-center gap-2 rounded-md border border-ctu-border bg-muted/30 px-3 py-1.5 text-sm font-medium text-ctu-text no-underline transition-colors hover:bg-primary hover:text-primary-foreground hover:border-primary"
                        >
                          {abbr && personFacultyColor && (
                            <span
                              className="inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-white"
                              style={{ backgroundColor: personFacultyColor }}
                            >
                              {abbr}
                            </span>
                          )}
                          <span>{person.name}</span>
                          <ArrowUpRight
                            className="h-3.5 w-3.5 opacity-40 transition-opacity group-hover:opacity-100"
                            aria-hidden
                          />
                        </a>
                      );
                    })}
                  </div>
                ) : (
                  <p className="m-0 text-sm text-ctu-text-muted italic">
                    {t('detail.noContributors')}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="mb-6 border-ctu-border shadow-none">
              <CardContent className="p-8">
                <div className="mb-4">
                  <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
                    {t('similar.heading')}
                  </span>
                  <p className="mt-1 text-sm text-ctu-text-muted">
                    {t('similar.subtitle')}
                  </p>
                </div>
                <SimilarResultsTable
                  results={similar}
                  parentPeopleIds={
                    new Set((project.people ?? []).map((p) => p.id))
                  }
                />
              </CardContent>
            </Card>
          </>
        )}
      </PageContent>
    </PageContainer>
  );
}
